import React, { useState, useContext, useEffect } from 'react';
import i18next from 'i18next';
import useStyles from 'style/js-style/components/thirdparties/ecovadis/EcovadisStyle';
import { FormLabel, Grid, TextField, Typography } from '@mui/material';
import EAChoiceSeparator from 'components/EAChoiceSeparator';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import EcovadisDialogButtons from '../EcovadisDialogButtons';
import EAIcon from 'components/common/EAIcon';
import { Context } from 'states/Store';
import { REGEXP_EMAIL, UserInvitationStatus } from 'services/common/Constants';
import EAAutoCompleteInput from 'components/inputs/EAAutoCompleteInput';
import { getAccountUsers } from 'services/edge/UserService';

const EcovadisEvaluationBuyerStep = ({
  onCancel = () => {},
  onValidate = () => {},
  isValidationLoading = false
}) => {
  const { classes } = useStyles();

  const [state] = useContext(Context);
  const [accountUsers, setAccountUsers] = useState([]);

  const {
    control,
    formState: { errors },
    clearErrors,
    setValue,
  } = useFormContext();

  let buyerId = useWatch({ name: 'buyerId' });

  // PrivilegeEnum.PRIVILEGE_VIEW_USER

  useEffect(() => {
    const selectedUser = accountUsers?.find((x) => x.id === buyerId);
    if (selectedUser) {
      clearErrors();
      setValue('buyer_contact_first_name', selectedUser.firstname || '');
      setValue('buyer_contact_last_name', selectedUser.name || '');
      setValue('buyer_contact_email', selectedUser.email || '');
    }
  }, [buyerId, accountUsers, clearErrors, setValue]);

  useEffect(() => {
    async function getUsers() {
      try {
        const result = await getAccountUsers(state.account.id);
        setAccountUsers(
          result.content
            .filter((user) => user.status === UserInvitationStatus.REGISTERED)
            .map((user) => {
              return {
                label: user.firstname + ' ' + user.name,
                value: user.id,
                id: user.id,
                name: user.name,
                firstname: user.firstname,
                email: user.email,
              };
            })
        );
      } catch (error) {
        setAccountUsers([]);
      }
    }
    if (state.account) {
      getUsers();
      setValue('buyerEntreprise', state.account.company);
    }
  }, [state.account, setValue]);

  return (
    <>
      <Typography className={classes.stepTitle}>
        {i18next.t('ecovadis.requestForm.contactStep.buyer.subTitle')}
      </Typography>

      <Grid container>
        <Grid item container>
          <Grid item xs={4} className={classes.choiceBlocLabel}>
            <EAIcon icon={'search'} />
            {i18next.t('ecovadis.requestForm.contactStep.chooseFromList')}
          </Grid>
          <Grid item container xs={8} className={classes.formContainer}>
            <Grid item xs={12} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t(
                  'ecovadis.requestForm.contactStep.buyer.buyerListLabel'
                )}
              </FormLabel>
              <EAAutoCompleteInput
                fieldName={'buyerId'}
                options={accountUsers}
                placeholder={i18next.t(
                  'ecovadis.requestForm.contactStep.buyer.buyerListPlaceholder'
                )}
                variant="outlined"
                limitTags={1}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={4}></Grid>
          <Grid item xs={8}>
            <div className={classes['separator-horizontal']}>
              <EAChoiceSeparator mode="horizontal" fullSize />
            </div>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={4} className={classes.choiceBlocLabel}>
            <EAIcon icon={'edit'} />
            <span>
              {i18next.t('ecovadis.requestForm.contactStep.manualInvitation')}
              <div className={classes.choiceBlocSubLabel}>
                {i18next.t('ecovadis.requestForm.contactStep.mandatoryFields')}
              </div>
            </span>
          </Grid>

          <Grid item container xs={8} className={classes.formContainer}>
            <Grid item xs={12} sm={6} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t('firstName')}
              </FormLabel>

              <Controller
                control={control}
                name="buyer_contact_first_name"
                rules={{
                  required: i18next.t('infos.required'),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="buyer_contact_first_name"
                    variant="outlined"
                    error={!!errors.buyer_contact_first_name}
                    helperText={
                      !!errors.buyer_contact_first_name
                        ? errors.buyer_contact_first_name.message
                        : ''
                    }
                    className={classes.input}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t('lastName')}
              </FormLabel>

              <Controller
                control={control}
                name="buyer_contact_last_name"
                rules={{
                  required: i18next.t('infos.required'),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="buyer_contact_last_name"
                    variant="outlined"
                    error={!!errors.buyer_contact_last_name}
                    helperText={
                      !!errors.buyer_contact_last_name
                        ? errors.buyer_contact_last_name.message
                        : ''
                    }
                    className={classes.input}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t(
                  'ecovadis.requestForm.contactStep.buyer.buyerEntreprise'
                )}
              </FormLabel>

              <Controller
                control={control}
                name="buyerEntreprise"
                rules={{
                  required: i18next.t('infos.required'),
                }}
                disabled
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="buyerEntreprise"
                    variant="outlined"
                    error={!!errors.buyerEntreprise}
                    helperText={
                      !!errors.buyerEntreprise
                        ? errors.buyerEntreprise.message
                        : ''
                    }
                    className={classes.input}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} className={classes.inputBloc}>
              <FormLabel className={classes.label}>
                {i18next.t('email')}
              </FormLabel>

              <Controller
                control={control}
                name="buyer_contact_email"
                rules={{
                  required: i18next.t('infos.required'),
                  pattern: {
                    value: REGEXP_EMAIL,
                    message: i18next.t('infos.USER_EMAIL_ERROR'),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="buyer_contact_email"
                    variant="outlined"
                    error={!!errors.buyer_contact_email}
                    helperText={
                      !!errors.buyer_contact_email
                        ? errors.buyer_contact_email.message
                        : ''
                    }
                    className={classes.input}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <EcovadisDialogButtons
        onCancel={onCancel}
        validateLabel={i18next.t('ecovadis.requestForm.sendEvaluation')}
        onValidate={onValidate}
        isLoading={isValidationLoading}
      />
    </>
  );
};

export default EcovadisEvaluationBuyerStep;
